// @givmo/board — the notice block: the backend's sentence, shown as written.
//
// This component is deliberately thin, and its thinness is the point. Everything a reader needs to
// judge the notice — the recorded period, the citation, the day count, the method used to count, and
// the zone it was counted in — is already inside `notice.statement`, composed server-side. The UI
// composes NOTHING: it renders that string, and where there is a shortfall it says how many days,
// which is the number the backend already computed.
//
// What must never appear here: a badge reading "properly noticed", a green check, a red error state,
// or any phrasing that turns the shortfall flag into a verdict. Whether a meeting was properly
// noticed is a legal conclusion (plan §3.4, and the terminology rail refuses the words). A shortfall
// is a number of days, flagged, and it never blocks anything.
//
// CBG-YR6-MEET: a committee meeting's notice is counted against the committee's own period, so its
// shortfall says whose period it is (review-188's F4); the board's line is unchanged.

import { Icon, testIdProp } from '@givmo/design-system';
import { Info } from 'lucide-react';
import type { BoardMeetingNoticeOut } from '@givmo/api-client';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';

export interface NoticeBlockProps {
  notice: BoardMeetingNoticeOut;
  /** A committee meeting's committee, whose own period the notice was counted against; or null. */
  committee?: string | null;
  testId?: string;
}

export function NoticeBlock({
  notice,
  committee = null,
  testId = 'meeting-notice',
}: NoticeBlockProps) {
  const shortfall = notice.is_short_of_recorded_period === true ? notice.shortfall_days : null;

  return (
    <div
      className="flex gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-16"
      {...testIdProp(testId)}
    >
      <Icon icon={Info} size={18} className="mt-2 shrink-0 text-icon-muted" aria-hidden />
      <div className="flex min-w-0 flex-col gap-6">
        {/* Verbatim. Never reworded, never summarized, never supplemented with a conclusion. */}
        <p
          className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp(`${testId}-statement`)}
        >
          {notice.statement}
        </p>
        {shortfall !== null ? (
          <p
            className="font-sans text-s13 font-w600 tracking-givmo text-text-strong"
            {...testIdProp(`${testId}-shortfall`)}
          >
            {committee !== null && typeof shortfall === 'number'
              ? cm.noticeShortfall(shortfall, committee)
              : `${shortfall} ${shortfall === 1 ? 'day' : 'days'} fewer than the period you recorded.`}
          </p>
        ) : null}
      </div>
    </div>
  );
}
